import { useEffect, useState, type CSSProperties } from 'react';

interface InlineSVGProps {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  /** Optional CSS color to override every fill in the source SVG. */
  color?: string;
}

const FILL_HEX_ATTR = /fill="#[0-9A-Fa-f]+"/g;
const FILL_HEX_DECL = /fill:#[0-9A-Fa-f]+/g;

/**
 * In-flight + persistent cache of inlined SVG sources. We deduplicate
 * concurrent fetches (so two `<InlineSVG src=".../logo.svg">` rendered in
 * the same tick share one network request) and keep the parsed text in
 * memory for instant subsequent renders. Cleared on full reload only.
 */
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

async function loadSVG(src: string): Promise<string> {
  const cached = cache.get(src);
  if (cached !== undefined) return cached;
  const ongoing = inflight.get(src);
  if (ongoing) return ongoing;
  const promise = fetch(src)
    .then((r) => r.text())
    .then((text) => {
      cache.set(src, text);
      inflight.delete(src);
      return text;
    })
    .catch((err) => {
      inflight.delete(src);
      throw err;
    });
  inflight.set(src, promise);
  return promise;
}

function recolour(svg: string, color: string | undefined): string {
  if (!color) return svg;
  return svg.replace(FILL_HEX_ATTR, `fill="${color}"`).replace(FILL_HEX_DECL, `fill:${color}`);
}

/**
 * Fetches the SVG source and inlines it as raw markup so that it can be
 * styled via `currentColor` and to bypass hosts that serve SVGs with the
 * wrong MIME type (e.g. as `text/plain`).
 *
 * Network requests are de-duplicated and cached in-process.
 */
export function InlineSVG({ src, alt, style, className, color }: InlineSVGProps) {
  const cacheHit = cache.get(src);
  const [svg, setSvg] = useState<string | null>(cacheHit ?? null);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(src);
    if (cached !== undefined) {
      setSvg(cached);
      return;
    }
    setSvg(null);
    loadSVG(src)
      .then((text) => {
        if (!cancelled) setSvg(text);
      })
      .catch(() => {
        /* swallow: a missing inline SVG should not crash the page */
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!svg) {
    return <span style={style} className={className} role="img" aria-label={alt} />;
  }

  return (
    <span
      role="img"
      aria-label={alt}
      style={style}
      className={className}
      dangerouslySetInnerHTML={{ __html: recolour(svg, color) }}
    />
  );
}
